import {Provider} from '@angular/core';
import {CanMatchFn} from '@angular/router';
import {of} from 'rxjs';

import {ProductFeedService} from '../../../../services/objects/product-feed.service';

const allowedFixtureStates = new Set(['truncated']);
const allowedFixturePathPattern = /^\/ai\/data-feeds\/[^/]+$/;

export function isProductFeedVisualFixtureUrl(href: string): boolean {
  const url = new URL(href);
  const isLocalHost = ['localhost', '127.0.0.1', '[::1]', '::1'].includes(url.hostname);
  const fixtureState = url.searchParams.get('productFeedVisualFixture') || '';

  return isLocalHost && allowedFixturePathPattern.test(url.pathname) && allowedFixtureStates.has(fixtureState);
}

export const productFeedVisualFixtureCanMatch: CanMatchFn = () => {
  if (typeof window === 'undefined' || !window.location) return false;

  return isProductFeedVisualFixtureUrl(window.location.href);
};

export const productFeedVisualFixtureProviders: Provider[] = [
  {
    provide: ProductFeedService,
    useValue: {
      getByIdAsync: () => of({
        clientId: 'fixture-client',
        id: 'fixture-feed',
        name: 'Fixture Product Feed',
        image: '',
        description: 'Local-only visual fixture for lucene snapshot review.',
        enabled: true,
        updatedDate: '2026-06-25T12:00:00Z',
        scheduleSettings: {
          scheduleEnabled: false,
          schedulingMode: 'manual',
          intervalMinutes: 0
        },
        notificationSettings: {
          emailNotificationsEnabled: false,
          emailNotificationSubject: '',
          emailNotificationRecipients: [],
          webhookNotificationsEnabled: false,
          webhookNotificationUrl: ''
        },
        type: 'Xml',
        skipForPageCollection: false,
        url: 'https://example.test/feed.xml',
        listingsDirectory: '/products/product',
        stripNamespace: '',
        urlProperty: 'link',
        crawlRegexPattern: '',
        urlRegexPattern: '',
        urlRegexReplacePattern: '',
        customCategorySyntax: '',
        defaultDomain: 'example.test',
        brandDomain: 'brand.example.test',
        market: 'Norway',
        isDefaultFeed: false,
        routeRules: [],
        lastFetchDateTime: '2026-06-25T10:30:00Z',
        fieldMapping: {
          price: {
            Attribute: 'content'
          }
        },
        filters: [
          {
            propertyName: 'availability',
            operation: 'equals',
            value: 'in stock'
          }
        ],
        transformPatterns: [
          {
            urlPattern: '*',
            transforms: [
              {
                transformer: 'HtmlParseSinglePropertyTransformer',
                name: 'price',
                primaryNode: '//meta[@itemprop="price"]',
                properties: {
                  Attribute: 'content'
                }
              }
            ]
          }
        ],
        luceneSnapshot: {
          generatedAt: '2026-06-25T12:00:00Z',
          sourceType: 'Xml',
          documentLimit: 10,
          fieldLimitPerDocument: 8,
          serializedJsonLimitBytes: 24576,
          stringValueLimit: 160,
          totalDocumentCount: 12,
          omittedFieldCount: 6,
          redactedValueCount: 3,
          truncation: {
            hasMoreDocuments: true,
            hasOmittedFields: true,
            hasRedactedValues: true,
            hasTrimmedStrings: true,
            hasStructuredValueSummaries: true,
            hitSerializedSizeLimit: true
          },
          documents: [
            {
              originalFieldCount: 10,
              omittedFieldCount: 4,
              redactedValueCount: 2,
              hasTrimmedStrings: true,
              hasStructuredValueSummaries: true,
              fields: [
                {name: 'sku', value: 'ava-tights'},
                {name: 'name', value: 'Ava Merino Base Layer'},
                {name: 'category', value: 'base-layers'},
                {name: 'price', value: '1299'},
                {name: 'availability', value: 'in stock'},
                {name: 'brand', value: '[redacted]'},
                {name: 'score', value: '1.234'},
                {name: 'sourceType', value: 'Xml'}
              ]
            },
            {
              originalFieldCount: 8,
              omittedFieldCount: 2,
              redactedValueCount: 1,
              hasTrimmedStrings: false,
              hasStructuredValueSummaries: true,
              fields: [
                {name: 'sku', value: 'kari-shorts'},
                {name: 'title', value: 'Kari Shorts Core Performance Edition'},
                {name: 'category', value: 'shorts'},
                {name: 'price', value: '899'},
                {name: 'currency', value: 'NOK'},
                {name: 'availability', value: 'out of stock'},
                {name: 'score', value: '0.998'},
                {name: 'sourceType', value: 'Xml'}
              ]
            }
          ]
        }
      }),
      getAllTasksAsync: () => of([]),
      saveAsync: () => of({}),
      deleteAsync: () => of({}),
      runProductFeedAsync: () => of({}),
      getTaskAsync: () => of({})
    }
  }
];
